import { type MaybeRef, type Ref, computed, nextTick, reactive, toValue, watch } from 'vue';
import { type AxiosError } from 'axios';
import { type PlaceAutocompleteModelDto } from '@/types/webapi';
import { useContainer } from '@/plugins/inversify';
import { IPlaceService, IPlaceServiceId } from '@/services';
import { useDisplay } from 'vuetify';
import PlaceAutocomplete from '../components/PlaceAutocomplete.vue';
import { useGeolocationHelper } from '@/features/common/composables';

export function usePlaceAutocomplete(wrapper: Ref<typeof PlaceAutocomplete>) {
    const element = computed<HTMLDivElement | undefined>(() => wrapper.value?.$el);
    const { mobile: isMobile } = useDisplay();

    function scrollIntoView(value: boolean) {
        if (!value || !isMobile.value) return;

        nextTick(() => element.value?.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'instant' }));
    }

    return {
        scrollIntoView
    };
}

export interface UsePlaceAutocompleteModelOptions {
    origin?: MaybeRef<PlaceAutocompleteModelDto | null>;
    stopPlacesOnly?: MaybeRef<boolean | undefined>;
}

export function usePlaceAutocompleteModel(options?: UsePlaceAutocompleteModelOptions) {
    const { getCurrentPosition, isAllowed } = useGeolocationHelper();
    const origin = computed(() => toValue(options?.origin));
    const stopPlacesOnly = computed(() => toValue(options?.stopPlacesOnly));

    // TODO: this is breaking storybook - refactor
    const container = useContainer();
    const placeService = container.get<IPlaceService>(IPlaceServiceId);

    const model = reactive({
        search: '',
        items: [] as PlaceAutocompleteModelDto[],
        loading: false,
        menu: false,
        focused: false
    });

    let abortController: AbortController | undefined;
    async function loadPlaceAutocompleteSuggestions(searchTerm: string) {
        try {
            abortController = new AbortController();
            model.loading = true;

            const { latitude: lat, longitude: lng } = origin.value?.coordinates || {};
            let _origin = lat && lng ? { lat, lng } : undefined;

            // if origin is not set, and geolocation is allowed, use current position as origin
            if (!_origin && isAllowed.value) {
                const position = await getCurrentPosition();
                if (position) {
                    _origin = { lat: position.coords.latitude, lng: position.coords.longitude };
                }
            }

            model.items = await placeService.getPlacesAutocomplete(searchTerm, {
                signal: abortController?.signal,
                origin: _origin,
                stopPlacesOnly: stopPlacesOnly.value
            });

            model.loading = false;
        } catch (e) {
            const { name } = e as AxiosError;

            if (name === 'CanceledError') return;

            model.loading = false;
            console.error(e as Error);
        }
    }

    watch(
        () => model.search,
        async value => {
            abortController?.abort();

            // when input field is defocused, the value is reset to '' so we should reset the loading state
            if (!value) {
                model.items = [];
                model.loading = false;
                return;
            }

            await loadPlaceAutocompleteSuggestions(value);
        }
    );

    return model;
}
